import { ApButtonMain, ApCheckbox } from '@alixpartners/ui-components';
import { SettlementEmailTemplate } from 'api/endpoints/settlementEmailTemplates';
import { InputText } from 'components/UIComponents/InputText';
import { ButtonClipboardCopy } from './components/ButtonClipboardCopy';
import { SettlementElementPanel } from './components/SettlementElementPanel';
import { SettlementEmailHeader } from './components/SettlementEmailHeader';
import { SettlementEmailHeaderDescription } from './components/SettlementEmailHeaderDescription';
import { SettlementEmailJoditEditor } from './components/SettlementEmailJoditEditor';
import { SettlementEmailRecipientDescription } from './components/SettlementEmailRecipientDescription';
import { SettlementEmailTmpSection } from './components/SettlementSectionPanel';
import './SettlementsEmailTemplatesContent.css';

const defaultEmptyTemplate: SettlementEmailTemplate = {
  id: 0,
  name: 'AwaitingClientReview',
  useAdditionalRecipients: false,
  displayName: 'Settlement template',
  includeAdminsToRecipients: false,
  includeIndividualsToRecipients: false,
  subject: '',
  template: '',
  tokens: [],
};

type SettlementChangeEvent = <T extends keyof SettlementEmailTemplate>(
  key: T,
  value: SettlementEmailTemplate[T],
) => void;

interface Props {
  value: SettlementEmailTemplate | undefined;
  canSave: boolean;
  onChange: (value: SettlementEmailTemplate) => void;
  onSaveClick: () => void;
  onRestoreClick: () => void;
}

export const SettlementsEmailTemplatesContent = ({
  value,
  canSave,
  onChange,
  onSaveClick,
  onRestoreClick,
}: Props) => {
  const template = value ?? defaultEmptyTemplate;

  const valueChange: SettlementChangeEvent = (key, value) => {
    const newValue: SettlementEmailTemplate = {
      ...template,
      [key]: value,
    };

    onChange(newValue);
  };

  return (
    <div className="settlement-email-template">
      <SettlementEmailHeader
        header={template.displayName}
        onRestoreClick={onRestoreClick}
      />
      <SettlementEmailHeaderDescription name={template.name} />

      <div className="settlement-email-template__divider" />

      {template.useAdditionalRecipients && (
        <AdditionalRecipientsSection
          template={template}
          onValueChange={valueChange}
        />
      )}

      <SettlementEmailTmpSection
        title="EMAIL STRUCTURE"
        className="settlement-email-template__email-structure"
      >
        <SettlementElementPanel title="Email Subject">
          <InputText
            className="settlement-email-template__subject"
            value={template.subject}
            onChange={(e) => valueChange('subject', e.currentTarget.value)}
          />
        </SettlementElementPanel>

        <SettlementElementPanel title="Tokens (Click to copy the token to the clipboard)">
          <div className="settlement-email-template__tokens">
            {template.tokens.map((r) => (
              <ButtonClipboardCopy
                key={r.name}
                clipboardText={r.expression}
                tooltip={r.tooltip}
              >
                {r.displayName}
              </ButtonClipboardCopy>
            ))}
          </div>
        </SettlementElementPanel>

        <SettlementElementPanel title="Message*">
          <SettlementEmailJoditEditor
            //Workaround: the "Undo" button is shown when template is changed (history is not cleared)
            key={template.id} //important!!! the "key" property is used to reinitialize the editor for every template (and clear history)
            text={template.template}
            onChange={(value) => valueChange('template', value)}
          />
        </SettlementElementPanel>
      </SettlementEmailTmpSection>

      {/* Save Button */}
      <div className="settlement-email-template__footer">
        <ApButtonMain onClick={onSaveClick} medium disabled={!canSave}>
          Save template
        </ApButtonMain>{' '}
      </div>
    </div>
  );
};

const AdditionalRecipientsSection = (props: {
  template: SettlementEmailTemplate;
  onValueChange: SettlementChangeEvent;
}) => {
  const { template, onValueChange } = props;

  return (
    <>
      <SettlementEmailTmpSection title="ADDITIONAL RECIPIENTS">
        <div className="settlement-email-template__description-important">
          If needed, please select any additional recipients for this email.
        </div>
        <SettlementEmailRecipientDescription name={template.name} />
      </SettlementEmailTmpSection>

      <div className="settlement-email-template__checkbox-group">
        <ApCheckbox
          id="email-checkbox-admin"
          checked={template.includeAdminsToRecipients}
          onChange={(e) =>
            onValueChange('includeAdminsToRecipients', e.target.checked)
          }
        >
          Admin
        </ApCheckbox>
        <ApCheckbox
          id="email-checkbox-individual"
          checked={template.includeIndividualsToRecipients}
          onChange={(e) =>
            onValueChange('includeIndividualsToRecipients', e.target.checked)
          }
        >
          Individual settlement email addresses
        </ApCheckbox>
      </div>

      <div className="settlement-email-template__divider" />
    </>
  );
};
